// Small arithmetic expression evaluator for Equation Box. Tokens are numbers,
// + - × ÷, parentheses, and an optional unknown 'x'. Division must be exact.

export type Tok = { t: 'n'; v: number } | { t: 'op'; v: '+' | '-' | '*' | '/' } | { t: '(' } | { t: ')' } | { t: 'x' };

export type EvalResult = { ok: true; value: number } | { ok: false; reason: string };

const PREC: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };

/** Evaluate a token list. Returns a reason when the expression is malformed. */
export function evaluate(tokens: Tok[], xValue?: number): EvalResult {
  if (!tokens.length) return { ok: false, reason: 'empty' };
  // shunting-yard to RPN with structural validation
  const out: Tok[] = [];
  const ops: Tok[] = [];
  let expectOperand = true;
  let depth = 0;
  for (const tk of tokens) {
    if (tk.t === 'n' || tk.t === 'x') {
      if (!expectOperand) return { ok: false, reason: 'two numbers in a row' };
      if (tk.t === 'x' && xValue === undefined) return { ok: false, reason: 'x has no value' };
      out.push(tk.t === 'x' ? { t: 'n', v: xValue! } : tk);
      expectOperand = false;
    } else if (tk.t === 'op') {
      if (expectOperand) return { ok: false, reason: 'a sign needs a number before it' };
      while (ops.length) {
        const top = ops[ops.length - 1];
        if (top.t === 'op' && PREC[top.v] >= PREC[tk.v]) out.push(ops.pop()!); else break;
      }
      ops.push(tk);
      expectOperand = true;
    } else if (tk.t === '(') {
      if (!expectOperand) return { ok: false, reason: 'a bracket needs a sign before it' };
      ops.push(tk); depth++;
    } else if (tk.t === ')') {
      if (expectOperand) return { ok: false, reason: 'empty bracket' };
      let found = false;
      while (ops.length) { const top = ops.pop()!; if (top.t === '(') { found = true; break; } out.push(top); }
      if (!found) return { ok: false, reason: 'a bracket is missing its opener' };
      depth--;
    }
  }
  if (expectOperand) return { ok: false, reason: 'it ends with a sign' };
  if (depth !== 0) return { ok: false, reason: 'a bracket is not closed' };
  while (ops.length) { const top = ops.pop()!; if (top.t === '(') return { ok: false, reason: 'a bracket is not closed' }; out.push(top); }
  // evaluate RPN
  const st: number[] = [];
  for (const tk of out) {
    if (tk.t === 'n') { st.push(tk.v); continue; }
    if (tk.t !== 'op') continue;
    const b = st.pop()!, a = st.pop()!;
    let v: number;
    switch (tk.v) {
      case '+': v = a + b; break;
      case '-': v = a - b; break;
      case '*': v = a * b; break;
      case '/':
        if (b === 0) return { ok: false, reason: 'you cannot divide by zero' };
        if (a % b !== 0) return { ok: false, reason: `${a} does not split evenly by ${b}` };
        v = a / b; break;
    }
    st.push(v!);
  }
  if (st.length !== 1) return { ok: false, reason: 'something is out of place' };
  return { ok: true, value: st[0] };
}

export function tokensToString(tokens: Tok[]): string {
  return tokens.map(t => t.t === 'n' ? String(t.v) : t.t === 'x' ? 'x' : t.t === 'op' ? ({ '+': '+', '-': '−', '*': '×', '/': '÷' })[t.v] : t.t).join(' ');
}
